import { Avatar, Button } from '@roger/ui-components';
import { ArrowLeft } from 'lucide-react';
import { useChannel } from '../../hooks/chat/useChatQueries';
import { initialsOf } from './initials';

// eslint-disable-next-line react-refresh/only-export-components -- the role-label map sits beside the one component that uses it
export const ROLE_LABELS: Record<string, string> = {
  case_manager: 'Case manager',
  clinician: 'Clinician',
  task_assignee: 'Assigned to a task',
};

function roleLine(roles: string[] | undefined): string {
  return (roles ?? [])
    .map(role => ROLE_LABELS[role])
    .filter(Boolean)
    .join(' · ');
}

/** Read-only. Membership is kept by the server; this only says where it is edited. */
export function MembersView({
  channelId,
  onBack,
  touch = false,
}: {
  channelId: string;
  onBack: () => void;
  /** Phone sheet: a 44 px Back. */
  touch?: boolean;
}) {
  const { data: header } = useChannel(channelId);

  // Only a group this caller can manage has a roster edited in Group Manager.
  // Automatic agency and clinic channels follow the roster, so no line.
  const membershipLine = header?.can_manage_group
    ? 'Admins edit members in Group Manager'
    : null;

  return (
    <>
      <div className='flex items-center gap-1 border-b px-2 py-2'>
        {/* autoFocus: the control that opened this view is gone, so keys need a home. */}
        <Button
          size={touch ? 'iconLg' : 'icon'}
          variant='ghost'
          aria-label='Back'
          title='Back'
          autoFocus
          onClick={onBack}
        >
          <ArrowLeft className='h-3.5 w-3.5' />
        </Button>
        <p className='flex-1 text-sm font-semibold'>Members</p>
      </div>
      <div className='flex-1 overflow-y-auto'>
        {(header?.members ?? []).map(member => (
          <div
            key={member.id}
            className='flex items-center gap-2 px-3 py-2 text-sm'
          >
            <Avatar size='sm' initials={initialsOf(member.name)} />
            <div className='min-w-0'>
              <div className='truncate'>{member.name}</div>
              {roleLine(member.roles) && (
                <span className='text-xs text-muted-foreground'>
                  {roleLine(member.roles)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {membershipLine && (
        <p className='border-t px-3 py-2 text-[11px] text-muted-foreground'>
          {membershipLine}
        </p>
      )}
      {header?.episode_id != null && !header.archived && (
        <p className='border-t px-3 py-2 text-[11px] text-muted-foreground'>
          Everyone assigned to this patient's episode, or to a visit, order or
          task on it, is in this group automatically. A new assignment adds the
          person on the next update.
        </p>
      )}
      {header?.episode_id != null && header.archived && (
        <p className='px-3 py-2 text-[11px] text-muted-foreground'>
          This episode is closed, so the members are frozen.
        </p>
      )}
    </>
  );
}
